import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {forkJoin, Observable, of, takeUntil} from 'rxjs';
import {HabilitationService} from '@core/auth/habilitation/habilitation.service';
import {CoverageService} from '@core/services/settings/coverage/coverage.service';
import {CoverageReferenceService} from '@core/services/settings/coverage-reference/coverage-reference.service';
import {InsuredRegistryService} from '@core/services/settings/insured-registry/insured-registry.service';
import {ProductionRegistryService} from '@core/services/settings/production-registry/production-registry.service';
import {IncompatibleCoverageService} from '@core/services/settings/incompatible-coverage/incompatible-coverage.service';
import {CoverageDurationService} from '@core/services/settings/coverage-duration/coverage-duration.service';
import {AccessoryService} from '@core/services/settings/accessory/accessory.service';
import {
  CommissionContributorService
} from '@core/services/settings/commission-contributor/commission-contributor.service';
import {DurationRateService} from '@core/services/settings/duration-rate/duration-rate.service';
import {ProductService} from '@core/services/settings/product/product.service';
import {
  CommissionPointOfSaleService
} from '@core/services/settings/commission-point-of-sale/commission-point-of-sale.service';
import {TaxTypeService} from '@core/services/settings/tax-type/tax-type.service';
import {TaxPrimeService} from '@core/services/settings/tax-primes/tax-primes.service';
import {TaxAccessoryService} from '@core/services/settings/tax-accessory/tax-accessory.service';
import {EndorsementService} from '@core/services/settings/endorsement/endorsement.service';
import {ReductionService} from '@core/services/settings/reduction/reduction.service';
import {
  MarketLevelOrganizationService
} from "@core/services/administration/market-level-organization/market-level-organization.service";
import {AttestationProductService} from "@core/services/settings/attestation-product/attestation-product.service";
import {Company} from "@core/services/administration/company/company.interface";
import {CompanyService} from "@core/services/administration/company/company.service";
import {ManagementEntity} from "@core/services/administration/management-entity/management-entity.interface";
import {ManagementEntityService} from "@core/services/administration/management-entity/management-entity.service";
import {EntityTypeCheck} from "@shared/utils/Helpers";
import {CommissionSubContributorService} from "@core/services/settings/commission-sub-contributor/commission-sub-contributor.service";
import {TaxCommissionsSubContributorService} from "@core/services/settings/commission-tax-sub-contributor/commission-tax-sub-contributor.service";

@Injectable({
  providedIn: 'root'
})
export class SettingsResolver implements Resolve<any> {

  IS_MLO = false

  /**
   * Constructor
   */
  constructor(
    private _habilitationService: HabilitationService,
    private _productService: ProductService,
    private _coverageService: CoverageService,
    private _coverageReferenceService: CoverageReferenceService,
    private _incompatibleCoverageService: IncompatibleCoverageService,
    private _productionRegistryService: ProductionRegistryService,
    private _insuredRegistryService: InsuredRegistryService,
    private _coverageDurationService: CoverageDurationService,
    private _accessoryService: AccessoryService,
    private _taxService: TaxTypeService,
    private _taxPrimeService: TaxPrimeService,
    private _taxAccessoryService: TaxAccessoryService,
    private _commissionService: CommissionPointOfSaleService,
    private _commissionContributorService: CommissionContributorService,
    private _endorsementService: EndorsementService,
    private _durationRateService: DurationRateService,
    private _reductionService: ReductionService,
    private _marketLevelOrganizationService: MarketLevelOrganizationService,
    private _companyService: CompanyService,
    private _attestationProductService: AttestationProductService,
    private _managementEntityService: ManagementEntityService,
    private _commissionSubContributorService: CommissionSubContributorService,
    private _taxCommissionsSubContributorService: TaxCommissionsSubContributorService,
  ) {

    this._managementEntityService.entity$
      .subscribe((entity: ManagementEntity) => {
        this.IS_MLO = EntityTypeCheck.isMarketLevelOrganization(entity)
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Use this resolver to resolve initial mock-api for the application
   *
   * @param route
   * @param state
   */
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> {
    // Fork join multiple API endpoint calls to wait all of them to finish

    const resolList: Observable<any>[] = [];

    if (this._habilitationService.has('VIEW_PRODUCT')) {
      resolList.push(this._productService.getAll());
    }

    if (this._habilitationService.has('VIEW_COVERAGES')) {
      resolList.push(this._marketLevelOrganizationService.getLinked());

      resolList.push(this._coverageService.getAll());
      resolList.push(this._coverageReferenceService.getAll());
      resolList.push(this._incompatibleCoverageService.getAll());
    }

    if (this._habilitationService.has('VIEW_VEHICLES')) {
      resolList.push(this._productionRegistryService.getAll());
    }

    if (this._habilitationService.has('VIEW_INSURED_REFERENTIALS')) {
      resolList.push(this._insuredRegistryService.getAll());
    }

    if (this._habilitationService.has('VIEW_DURATIONS')) {
      resolList.push(this._coverageDurationService.getAll());
    }

    resolList.push(this._endorsementService.getAll());

    resolList.push(this._accessoryService.getAll());

    resolList.push(this._taxPrimeService.getAll());
    resolList.push(this._taxAccessoryService.getAll());

    resolList.push(this._attestationProductService.getAll());

    resolList.push(this._taxService.getAll());

    resolList.push(this._commissionService.getAllPrimes());
    resolList.push(this._commissionService.getAllAccessories());

    resolList.push(this._commissionContributorService.getAllPrimes());
    resolList.push(this._commissionContributorService.getAllAccessories());

    resolList.push(this._commissionSubContributorService.getAllPrimes());
    resolList.push(this._commissionSubContributorService.getAllAccessories());
    resolList.push(this._taxCommissionsSubContributorService.getAll());

    resolList.push(this._durationRateService.getAll());

    resolList.push(this._reductionService.getAll());

    if (this.IS_MLO) {
      resolList.push(this._companyService.getLinked());
    }

    return forkJoin(resolList);
  }
}
